"use strict";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { UniverseChainId } from "features/chains/types";
import { fetchVeTokenPricesUsd } from "features/veSubgraph/tokenPrices";
import { isVeSubgraphAvailable } from "features/veSubgraph/client";
import { useMemo } from "react";
const CHAIN_ID_TO_DEXSCREENER = {
  [UniverseChainId.Mainnet]: "bsc",
  [UniverseChainId.Sepolia]: "bsc"
};
async function fetchTokenPriceUsd(chainId, tokenAddress) {
  const dexScreenerChainId = CHAIN_ID_TO_DEXSCREENER[chainId];
  if (!dexScreenerChainId) {
    return void 0;
  }
  const response = await fetch(`https://api.dexscreener.com/tokens/v1/${dexScreenerChainId}/${tokenAddress}`);
  if (!response.ok) {
    throw new Error(`DexScreener API error: ${response.status} ${response.statusText}`);
  }
  const pairs = await response.json();
  if (!pairs || pairs.length === 0) {
    return void 0;
  }
  const pair = pairs[0];
  const normalized = tokenAddress.toLowerCase();
  const isBaseToken = pair.baseToken.address.toLowerCase() === normalized;
  if (isBaseToken) {
    const price = parseFloat(pair.priceUsd);
    return isNaN(price) ? void 0 : price;
  }
  const basePrice = parseFloat(pair.priceUsd);
  const priceNative = parseFloat(pair.priceNative);
  if (isNaN(basePrice) || isNaN(priceNative) || priceNative <= 0) {
    return void 0;
  }
  return basePrice / priceNative;
}
async function fetchPriceMap(chainId, addresses) {
  const priceMap = /* @__PURE__ */ new Map();
  if (addresses.length === 0) {
    return priceMap;
  }
  if (isVeSubgraphAvailable(chainId)) {
    try {
      const vePrices = await fetchVeTokenPricesUsd(chainId, addresses);
      vePrices.forEach((price, address) => priceMap.set(address, price));
    } catch (error) {
      console.warn("[useTokenPriceMap] ve subgraph pricing failed, falling back to DexScreener:", error);
    }
  }
  const missing = addresses.filter((address) => !priceMap.has(address));
  if (missing.length > 0) {
    const results = await Promise.allSettled(missing.map((address) => fetchTokenPriceUsd(chainId, address)));
    results.forEach((result, index) => {
      if (result.status === "fulfilled" && result.value !== void 0 && !isNaN(result.value)) {
        priceMap.set(missing[index], result.value);
      }
    });
  }
  return priceMap;
}
export function useTokenPriceMap(chainId, addresses) {
  const uniqueAddresses = useMemo(
    () => Array.from(new Set(addresses.filter(Boolean).map((a) => a.toLowerCase()))).sort(),
    [addresses]
  );
  const query = useQuery({
    queryKey: ["token-price-map", chainId, uniqueAddresses],
    queryFn: () => fetchPriceMap(chainId, uniqueAddresses),
    enabled: uniqueAddresses.length > 0,
    staleTime: 3e4,
    gcTime: 3e5,
    retry: 2,
    // The portfolio's address set grows as balances/positions stream in, and
    // each growth is a new query key — keep serving the previous map so
    // already-priced rows don't flash to "no price" while the superset loads.
    placeholderData: keepPreviousData
  });
  return useMemo(
    () => ({
      priceMap: query.data ?? /* @__PURE__ */ new Map(),
      isLoading: query.isLoading,
      isError: query.isError
    }),
    [query.data, query.isLoading, query.isError]
  );
}
